import { useState } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";
import FollowersList from "./FollowersList";

/**
 * FollowersModal component for displaying followers/following lists in a modal
 *
 * Features:
 * - Modal display for followers and following lists
 * - Tab switching between followers and following
 * - Search functionality for filtering users
 * - Responsive design for mobile and desktop
 * - Integration with FollowersList component
 *
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5, 4.6
 */
const FollowersModal = ({
  isOpen,
  onClose,
  userId,
  username,
  initialTab = "followers",
  followerCount = 0,
  followingCount = 0,
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [searchQuery, setSearchQuery] = useState("");

  if (!isOpen) return null;

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchQuery(""); // Clear search when switching tabs
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] flex flex-col mx-4">
        {/* Header */}
        <div className="p-6 border-b border-light-grey">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-bold text-dark-grey">
                {username || "User"}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-medium-grey hover:text-dark-grey transition-colors duration-200"
              aria-label="Close modal"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="flex space-x-1 bg-light-grey rounded-lg p-1 mb-4">
            <button
              onClick={() => handleTabChange("followers")}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                activeTab === "followers"
                  ? "bg-white text-dark-grey shadow-sm"
                  : "text-medium-grey hover:text-dark-grey"
              }`}
            >
              Followers ({followerCount})
            </button>
            <button
              onClick={() => handleTabChange("following")}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                activeTab === "following"
                  ? "bg-white text-dark-grey shadow-sm"
                  : "text-medium-grey hover:text-dark-grey"
              }`}
            >
              Following ({followingCount})
            </button>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FontAwesomeIcon
                icon={faSearch}
                className="h-4 w-4 text-medium-grey"
              />
            </div>
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              value={searchQuery}
              onChange={handleSearchChange}
              className="block w-full pl-10 pr-10 py-2 border border-light-grey rounded-md focus:ring-2 focus:ring-primary focus:border-primary text-sm"
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-medium-grey hover:text-dark-grey"
              >
                <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <FollowersList
            userId={userId}
            type={activeTab}
            compact={true}
            searchQuery={searchQuery}
            className="min-h-0"
            onUserClick={() => {
              // Close modal when user navigates to a profile
              onClose();
            }}
          />
        </div>
      </div>
    </div>
  );
};

FollowersModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
  username: PropTypes.string,
  initialTab: PropTypes.oneOf(["followers", "following"]),
  followerCount: PropTypes.number,
  followingCount: PropTypes.number,
};

export default FollowersModal;
